import { useState } from 'react'
import { match } from 'ts-pattern'
import {
  IconArchive,
  IconEllipsisHorizontal,
  IconPencil,
  IconTag,
  Button,
  SequenceMenu,
  GreyGrey60,
  RedRed50,
  Center
} from '@sequencehq/core-components'

import { UseContactTableInterface } from './useContactTable'
import { CustomerContact } from 'CustomerContacts/types'
import { EditContactModal } from '../../modals/EditContactModal'
import { MarkAsPrimaryBillingContactDialog } from '../../modals/MarkAsPrimaryBillingContactDialog'
import { RemoveContactDialog } from '../../modals/RemoveContactDialog'
import { MarkAsShippingContactDialog } from '../../modals/MarkAsShippingContactDialog'
import { UnmarkShippingContactDialog } from '../../modals/UnmarkShippingContactDialog'
import { ProtectedComponent } from 'lib/rbac'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import {
  isPrimaryBillingContact,
  isShippingContact
} from 'CustomerContacts/utils/contactPurposes'

type ActiveModal =
  | 'edit'
  | 'markPrimary'
  | 'markShipping'
  | 'unmarkShipping'
  | 'archive'

export const KebabMenu = ({
  contact,
  hook: {
    editContact,
    shippingContact,
    markAsPrimaryBillingContact,
    markAsShippingContact,
    unmarkAsShippingContact,
    archiveContact
  }
}: {
  contact: CustomerContact
  hook: UseContactTableInterface
}) => {
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null)
  const closeModal = () => setActiveModal(null)

  const { useCustomerShippingAddresses } = useFeatureFlags()

  const isPrimaryContact = isPrimaryBillingContact(contact)
  const isShipping = isShippingContact(contact)

  return (
    <ProtectedComponent permission="org.customer:update">
      <SequenceMenu
        data-testid={`contacts.row.${contact.id}.menu`}
        width={226}
        alignment="bottom-right"
        items={[
          {
            uuid: 'edit',
            label: 'Edit contact',
            iconLeft: <IconPencil />,
            'data-testid': `contacts.row.${contact.id}.menu.edit`,
            onClick: () => {
              editContact.editContactById(contact.id)
              setActiveModal('edit')
            }
          },
          ...(isPrimaryContact
            ? []
            : [
                {
                  uuid: 'markPrimary',
                  label: 'Mark as primary billing contact',
                  iconLeft: <IconTag />,
                  'data-testid': `contacts.row.${contact.id}.menu.markPrimary`,
                  onClick: () => setActiveModal('markPrimary')
                }
              ]),
          ...(useCustomerShippingAddresses
            ? [
                isShipping
                  ? {
                      uuid: 'unmarkShipping',
                      label: 'Unmark as shipping contact',
                      iconLeft: <IconTag />,
                      'data-testid': `contacts.row.${contact.id}.menu.unmarkShipping`,
                      onClick: () => setActiveModal('unmarkShipping')
                    }
                  : {
                      uuid: 'markShipping',
                      label: 'Mark as shipping contact',
                      iconLeft: <IconTag />,
                      'data-testid': `contacts.row.${contact.id}.menu.markShipping`,
                      onClick: () => setActiveModal('markShipping')
                    }
              ]
            : []),
          ...(isPrimaryContact
            ? []
            : [
                {
                  uuid: 'archive',
                  label: 'Archive contact',
                  iconLeft: <IconArchive />,
                  style: { color: RedRed50 },
                  'data-testid': `contacts.row.${contact.id}.menu.archive`,
                  onClick: () => setActiveModal('archive')
                }
              ])
        ]}
        menuButton={
          <Button variant="ghost" size="24px">
            <Center flexShrink="0">
              <IconEllipsisHorizontal color={GreyGrey60} />
            </Center>
          </Button>
        }
      />
      {match(activeModal)
        .with('edit', () => (
          <EditContactModal
            isOpen
            contactId={contact.id}
            onClose={closeModal}
          />
        ))
        .with('markPrimary', () => (
          <MarkAsPrimaryBillingContactDialog
            isOpen
            title={`Mark ${contact.email} as primary billing contact`}
            onConfirm={() => markAsPrimaryBillingContact(contact.id)}
            onClose={closeModal}
          />
        ))
        .with('markShipping', () => (
          <MarkAsShippingContactDialog
            isOpen
            title={`Mark ${contact.email} as shipping contact`}
            previousShippingContactEmail={shippingContact?.email}
            onConfirm={() => markAsShippingContact(contact.id)}
            onClose={closeModal}
          />
        ))
        .with('unmarkShipping', () => (
          <UnmarkShippingContactDialog
            isOpen
            title={`Unmark ${contact.email} as shipping contact`}
            onConfirm={() => unmarkAsShippingContact(contact.id)}
            onClose={closeModal}
          />
        ))
        .with('archive', () => (
          <RemoveContactDialog
            isOpen
            title={`Archive ${contact.email}`}
            onConfirm={() => archiveContact(contact.id)}
            onClose={closeModal}
          />
        ))
        .with(null, () => null)
        .exhaustive()}
    </ProtectedComponent>
  )
}
